import { useDisclosure } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import useCheckCurrentDRNumber from '../../queries/useCheckCurrentDRNumber';
import useGetAvailableDRItems from '../../queries/useGetAvailableDRItems';
import useGetAvailableNumbersDropdown from '../../queries/useGetAvailableNumbersDropdown';
import UserCollapsableAttributes from '../formFields/UserCollapsableAttributes';
import UserPlatformControl from '../formFields/UserPlatformControl';
import UserPlatformDialPlanDropdown from '../formFields/UserPlatformDialPlanDropdown';
import UserPlatformNumberDropdown from '../formFields/UserPlatformNumberDropdown';
import UserPlatformVoicePolicyDropdown from '../formFields/UserPlatformVoicePolicyDropdown';
import UserPlatformConvertRa from '../formFields/UserPlatformConvertRa';

import ConvertRaModal from 'pages/accounts/users/new/modal/ConvertRaModal';
import walkthroughIds from 'pages/accounts/users/walkthroughIds';
import useConvertRaToUser from 'pages/accounts/users/queries/useUpdateConvertRa';

const UserTeamsSection = ({
    submitData,
    setSubmitData,
    errors,
    touched,
    platformUser,
    section = 'teams',
}) => {
    const finalRef = useRef(null);
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { requiredScope: accountScope } = useSelector((state) => state.login);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const platformIndex = submitData.findIndex(
        (platform) => platform.platformID === platformUser?.platformID,
    );
    const platform = submitData[platformIndex];
    const numberId = platform?.userProfiles[0]?.numberID;

    const [voicePolicies, setVoicePolicies] = useState([]);
    const [dialPlans, setDialPlans] = useState([]);
    const [isDrNumber, setIsDrNumber] = useState(false);
    const [platformNumbers, setPlatformNumbers] = useState([]);
    const [selectedlNumber, setSelectedNumber] = useState(undefined);
    const [isNumberChecked, setIsNumberChecked] = useState(false);

    const platformNumber = platformUser?.userProfiles[0]?.platformNumber;
    const RaID =
        platformUser?.userType === 'ResourceAccount' ? platformUser?.id : null;
    const isRA = Boolean(RaID);

    const {
        mutate: handleConvert,
        loading,
        data: updatedPlatformUser,
    } = useConvertRaToUser({
        onClose,
        platformUserId: RaID,
        userType: 'User',
    });
    const isLoading = loading === 'loading';
    const isUser = updatedPlatformUser?.userType === 'User';
    const isRaConvertHidden = isUser || !isRA;

    const { isFetching: isNumberChecking } = useCheckCurrentDRNumber(
        selectedlNumber || platformNumber,
        setSelectedNumber,
        setIsNumberChecked,
        setIsDrNumber,
        platformUser?.platformID,
        accountScope,
    );

    const { isFetching: isNumbersFetching, refetch } =
        useGetAvailableNumbersDropdown(
            currentCompany,
            platformNumber,
            platformUser?.platformID,
            setPlatformNumbers,
            {
                enabled: isNumberChecked,
            },
        );

    const { isFetching: isDrFetching } = useGetAvailableDRItems(
        numberId,
        setVoicePolicies,
        setDialPlans,
        setIsDrNumber,
        isDrNumber,
        platformUser?.platformID,
        accountScope,
        currentCompany,
    );

    useEffect(() => {
        if (!isNumberChecking && isNumberChecked) {
            refetch();
        }
    }, [isNumberChecking, isNumberChecked, selectedlNumber, refetch]);

    const fields = [
        <UserPlatformControl
            key={`${section}-platform`}
            submitData={submitData}
            setPlatformUser={setSubmitData}
            errors={errors}
            touched={touched}
            darkMode={darkMode}
            section={section}
            platformID={platformUser?.platformID}
        />,
        platformUser?.managedBy === 0 && platformUser?.isLicensed && (
            <UserPlatformNumberDropdown
                key={`${section}-number`}
                setPlatformUser={setSubmitData}
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                section={section}
                platformIndex={platformIndex}
                platform={platform}
                platformNumbers={platformNumbers}
                isLoading={isNumberChecking || isNumbersFetching}
                setSelectedNumber={setSelectedNumber}
            />
        ),
        platformUser?.managedBy === 0 &&
            isDrNumber &&
            platformUser?.isLicensed && (
                <UserPlatformVoicePolicyDropdown
                    key={`${section}-voice-policy`}
                    setPlatformUser={setSubmitData}
                    errors={errors}
                    darkMode={darkMode}
                    platformIndex={platformIndex}
                    platform={platform}
                    voicePolicies={voicePolicies}
                    isLoading={isDrFetching}
                />
            ),
        platformUser?.managedBy === 0 &&
            platform?.userType === 'User' &&
            isDrNumber &&
            platformUser?.isLicensed && (
                <UserPlatformDialPlanDropdown
                    key={`${section}-dial-plan`}
                    setPlatformUser={setSubmitData}
                    errors={errors}
                    darkMode={darkMode}
                    platformIndex={platformIndex}
                    platform={platform}
                    dialPlans={dialPlans}
                    isLoading={isDrFetching}
                />
            ),
        <UserCollapsableAttributes
            key={`${section}-collapsable-attributes`}
            index={5}
            errors={errors}
            touched={touched}
            darkMode={darkMode}
            platformIndex={platformIndex}
            isLastItem={true}
        />,

        !isRaConvertHidden && (
            <>
                <UserPlatformConvertRa
                    onOpen={onOpen}
                    darkMode={darkMode}
                    walkthroughIds={walkthroughIds}
                />
                <ConvertRaModal
                    {...{
                        finalRef,
                        handleConvert,
                        isLoading,
                        isOpen,
                        onClose,
                        walkthroughIds,
                    }}
                />
            </>
        ),
    ].filter(Boolean);
    return fields;
};

export default UserTeamsSection;
